/* Mark's Outdoors homepage behaviors — vanilla ports from home-sections.tsx.
   One file, each init guarded by element presence:
   - magic text (scroll-linked word reveal, OurStory)
   - brands marquee (rAF, speed eases 1 → 0.25 on hover)
   - Shop by Adventure expanding tiles
   - reviews rotator (portrait stack + word-by-word quote)
   - stats band (one-time count-up, rotation, deer-season countdown)
   - journal horizontal scroller (snap + next arrow)
   - Alabama field guide map (activity switch + pin popups)
   All respect prefers-reduced-motion. */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Magic text (word-by-word scroll reveal) ---------- */
  function initMagicText(scope) {
    (scope || document).querySelectorAll('[data-magic-text]').forEach(function (el) {
      if (el.__mo) return;
      el.__mo = true;
      // Split on authored <br> (from newline_to_br) so a line break survives the
      // word-span rebuild; each line's words still reveal one at a time.
      var lines = el.innerHTML.split(/<br\s*\/?>/i);
      el.textContent = '';
      var fills = [];
      lines.forEach(function (lineHtml, li) {
        if (li > 0) el.appendChild(document.createElement('br'));
        var tmp = document.createElement('div');
        tmp.innerHTML = lineHtml;
        var text = (tmp.textContent || '').trim();
        if (!text) return;
        text.split(/\s+/).forEach(function (w) {
          var span = document.createElement('span');
          span.className = 'mo-magic-word';
          var ghost = document.createElement('span');
          ghost.className = 'mo-magic-word__ghost';
          ghost.setAttribute('aria-hidden', 'true');
          ghost.textContent = w;
          var fill = document.createElement('span');
          fill.className = 'mo-magic-word__fill';
          fill.textContent = w;
          span.appendChild(ghost);
          span.appendChild(fill);
          el.appendChild(span);
          // Real whitespace between word spans so the line has break opportunities
          // and can wrap (a CSS margin alone is not a break opportunity → overflow).
          el.appendChild(document.createTextNode(' '));
          fills.push(fill);
        });
      });
      if (reduced) {
        fills.forEach(function (f) { f.style.opacity = '1'; });
        return;
      }
      var update = function () {
        var r = el.getBoundingClientRect();
        var vh = window.innerHeight;
        // Progress 0 → 1 as the element's top travels from 90% to 25% of the
        // viewport (matches useScroll offset ["start 0.9", "start 0.25"]).
        var p = (vh * 0.9 - r.top) / (vh * 0.9 - vh * 0.25);
        p = Math.min(Math.max(p, 0), 1);
        fills.forEach(function (f, i) {
          var start = i / fills.length;
          var o = (p - start) * fills.length;
          f.style.opacity = Math.min(Math.max(o, 0), 1);
        });
      };
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
    });
  }

  /* ---------- Brands marquee ---------- */
  function initMarquee(scope) {
    (scope || document).querySelectorAll('[data-mo-marquee]').forEach(function (reel) {
      if (reel.__mo) return;
      reel.__mo = true;
      var track = reel.querySelector('.mo-brands__track');
      if (!track || reduced) return;
      var speedTarget = 1;
      var speedNow = 1;
      var offset = 0;
      var last = performance.now();
      reel.addEventListener('mouseenter', function () { speedTarget = 0.25; });
      reel.addEventListener('mouseleave', function () { speedTarget = 1; });
      var tick = function (now) {
        var dt = Math.min((now - last) / 1000, 0.1);
        last = now;
        speedNow += (speedTarget - speedNow) * Math.min(1, dt * 5);
        var half = track.scrollWidth / 2;
        if (half > 0) {
          offset -= (half / 50) * speedNow * dt;
          if (-offset >= half) offset += half;
          track.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  /* ---------- Shop by Adventure tiles ---------- */
  function initAdventure(scope) {
    (scope || document).querySelectorAll('[data-mo-adventure]').forEach(function (host) {
      if (host.__mo) return;
      host.__mo = true;
      var tiles = host.querySelectorAll('.mo-adventure__tile');
      tiles.forEach(function (tile) {
        var activate = function () {
          tiles.forEach(function (t) { t.classList.toggle('is-active', t === tile); });
        };
        var deactivate = function () { tile.classList.remove('is-active'); };
        tile.addEventListener('mouseenter', activate);
        tile.addEventListener('mouseleave', deactivate);
        tile.addEventListener('focusin', activate);
        tile.addEventListener('focusout', function (e) {
          if (!tile.contains(e.relatedTarget)) deactivate();
        });
      });
    });
  }

  /* ---------- Reviews rotator ---------- */
  function initReviews(scope) {
    (scope || document).querySelectorAll('[data-mo-reviews]').forEach(function (host) {
      if (host.__mo) return;
      host.__mo = true;
      var portraits = host.querySelectorAll('.mo-reviews__portrait');
      var quotes = JSON.parse(host.querySelector('[data-reviews-data]').textContent);
      var quoteEl = host.querySelector('.mo-reviews__quote');
      var nameEl = host.querySelector('.mo-reviews__attr-name');
      var metaEl = host.querySelector('.mo-reviews__attr-meta');
      var counterEl = host.querySelector('[data-reviews-counter]');
      var count = quotes.length;
      if (!count) return;
      var index = 0;

      function pad(n) { return String(n).padStart(2, '0'); }

      function renderQuote(q) {
        quoteEl.textContent = '';
        quoteEl.classList.remove('is-animating');
        q.quote.split(/\s+/).forEach(function (w, i) {
          var s = document.createElement('span');
          s.className = 'mo-word';
          s.textContent = w;
          if (!reduced) s.style.animationDelay = i * 20 + 'ms';
          quoteEl.appendChild(s);
        });
        if (!reduced) {
          void quoteEl.offsetWidth;
          quoteEl.classList.add('is-animating');
        }
        nameEl.textContent = q.name;
        metaEl.textContent = q.meta;
        if (counterEl) counterEl.textContent = pad(index + 1) + ' / ' + pad(count);
      }

      function render() {
        portraits.forEach(function (p, i) {
          p.classList.toggle('is-active', i === index);
          p.style.zIndex = i === index ? 20 : 20 - Math.abs(i - index);
        });
        renderQuote(quotes[index]);
      }

      host.querySelectorAll('[data-reviews-prev]').forEach(function (b) {
        b.addEventListener('click', function () {
          index = (index - 1 + count) % count;
          render();
        });
      });
      host.querySelectorAll('[data-reviews-next]').forEach(function (b) {
        b.addEventListener('click', function () {
          index = (index + 1) % count;
          render();
        });
      });
      render();
    });
  }

  /* ---------- Stats band ---------- */
  function initStats(scope) {
    (scope || document).querySelectorAll('[data-mo-stats]').forEach(function (host) {
      if (host.__mo) return;
      host.__mo = true;
      var facts = JSON.parse(host.querySelector('[data-stats-data]').textContent);

      // Deer-season countdown fact goes first. Clamps at zero → "OPEN".
      var openerDate = host.getAttribute('data-season-opener');
      if (openerDate) {
        var opener = new Date(openerDate + 'T00:00:00');
        if (!isNaN(opener.getTime())) {
          var days = Math.max(0, Math.ceil((opener.getTime() - Date.now()) / 86400000));
          facts.unshift(
            days > 0
              ? { label: host.getAttribute('data-season-label') || 'Days to deer season', value: days }
              : { label: host.getAttribute('data-season-open-label') || 'Deer season', display: 'OPEN' }
          );
        }
      }

      var slots = host.querySelectorAll('[data-stat-slot]');
      var offset = 0;
      var phase = 'idle';

      function factHtml(f, counted) {
        if (f.value != null) {
          var v = counted != null ? counted : f.value;
          return v.toLocaleString('en-US') + (f.suffix || '');
        }
        return f.display || '';
      }

      function paint() {
        slots.forEach(function (slot, i) {
          var f = facts[(i + offset) % facts.length];
          slot.querySelector('.mo-stats__value').textContent = factHtml(f);
          slot.querySelector('.mo-stats__fact-label').textContent = f.label;
        });
      }

      function countUp() {
        var duration = 1400;
        var t0 = performance.now();
        var tick = function (now) {
          var p = Math.min(1, (now - t0) / duration);
          var eased = 1 - Math.pow(1 - p, 3);
          slots.forEach(function (slot, i) {
            var f = facts[i % facts.length];
            if (f.value != null) {
              slot.querySelector('.mo-stats__value').textContent =
                Math.round(f.value * eased).toLocaleString('en-US') + (f.suffix || '');
            }
          });
          if (p < 1) requestAnimationFrame(tick);
          else window.setTimeout(startRotation, 1800 - duration > 0 ? 1800 - duration : 400);
        };
        requestAnimationFrame(tick);
      }

      function startRotation() {
        phase = 'rotating';
        if (reduced) return;
        window.setInterval(function () {
          slots.forEach(function (slot) { slot.querySelector('.mo-stats__fact').classList.add('is-fading'); });
          window.setTimeout(function () {
            offset += 1;
            paint();
            slots.forEach(function (slot) { slot.querySelector('.mo-stats__fact').classList.remove('is-fading'); });
          }, 450);
        }, 4000);
      }

      paint();
      var io = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting && phase === 'idle') {
            phase = 'counting';
            io.disconnect();
            if (reduced) startRotation();
            else countUp();
          }
        },
        { threshold: 0.35 }
      );
      io.observe(host);
    });
  }

  /* ---------- Journal scroller ---------- */
  function initJournal(scope) {
    (scope || document).querySelectorAll('[data-mo-journal]').forEach(function (host) {
      if (host.__mo) return;
      host.__mo = true;
      var scroller = host.querySelector('.mo-journal__scroller');
      var nextBtn = host.querySelector('.mo-journal__next');
      if (!scroller || !nextBtn) return;
      var update = function () {
        nextBtn.hidden = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 4;
      };
      update();
      scroller.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      nextBtn.addEventListener('click', function () {
        var card = scroller.querySelector('.mo-journal-card');
        var step = card ? card.offsetWidth + 20 : scroller.clientWidth * 0.85;
        scroller.scrollBy({ left: step, behavior: reduced ? 'auto' : 'smooth' });
      });
    });
  }

  /* ---------- Alabama field guide map ---------- */
  function initMap(scope) {
    (scope || document).querySelectorAll('[data-mo-map]').forEach(function (host) {
      if (host.__mo) return;
      host.__mo = true;
      var activities = host.querySelectorAll('[data-map-activity]');
      var pins = host.querySelectorAll('[data-map-pin]');
      var popup = host.querySelector('.mo-map__popup');
      var pinCounter = host.querySelector('[data-map-pin-count]');
      var activeActivity = activities.length ? activities[0].getAttribute('data-map-activity') : null;
      var activeSpot = null;

      function renderPins() {
        var visible = 0;
        pins.forEach(function (pin) {
          var match = pin.getAttribute('data-activity') === activeActivity;
          pin.style.display = match ? '' : 'none';
          if (match) visible++;
          var dot = pin.querySelector('[data-pin-dot]');
          var isActive = activeSpot === pin;
          dot.setAttribute('r', isActive ? 6 : 4);
          dot.style.fill = isActive ? 'var(--amber-bright)' : 'var(--brick)';
        });
        if (pinCounter) pinCounter.textContent = String(visible).padStart(2, '0') + ' / PINS';
      }

      function closePopup() {
        activeSpot = null;
        popup.hidden = true;
        renderPins();
      }

      function openPopup(pin) {
        activeSpot = pin;
        popup.querySelector('.mo-map__popup-name').textContent = pin.getAttribute('data-name');
        popup.querySelector('.mo-map__popup-meta').textContent =
          pin.getAttribute('data-region') + ' · ' + pin.getAttribute('data-coords');
        popup.querySelector('.mo-map__popup-note').textContent = pin.getAttribute('data-note');
        var x = parseFloat(pin.getAttribute('data-x'));
        var y = parseFloat(pin.getAttribute('data-y'));
        popup.style.left = 'min(max(' + x + '%, 12%), 66%)';
        popup.style.top = y + '%';
        popup.hidden = false;
        renderPins();
      }

      pins.forEach(function (pin) {
        var toggle = function () {
          if (activeSpot === pin) closePopup();
          else openPopup(pin);
        };
        pin.addEventListener('click', toggle);
        pin.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        });
      });

      popup.querySelector('.mo-map__popup-close').addEventListener('click', closePopup);

      activities.forEach(function (btn) {
        btn.addEventListener('click', function () {
          activeActivity = btn.getAttribute('data-map-activity');
          activities.forEach(function (b) {
            b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
          });
          closePopup();
        });
      });

      renderPins();
    });
  }

  function initAll(scope) {
    initMagicText(scope);
    initMarquee(scope);
    initAdventure(scope);
    initReviews(scope);
    initStats(scope);
    initJournal(scope);
    initMap(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
